"use client";

import { useTranslations } from "next-intl";
import type { FilterWithFacet } from "../useFacets";
import { useSelectedFilters } from "../useSelectedFilters";

/** An ISO timestamp as the `yyyy-mm-dd` a date input takes. */
function asDate(value: string | null | undefined): string {
  return typeof value === "string" ? value.slice(0, 10) : "";
}

/**
 * A date range: a start and an end.
 *
 * Native date inputs rather than the legacy calendar. That calendar was
 * `react-dates`, which is deprecated and React 16-only — it can't run here at
 * all, and the ~40 CSS overrides it needed were overriding a widget that no
 * longer exists. The browser's own picker is localised, keyboard-accessible and
 * familiar on mobile, which the ported one was not.
 *
 * The bounds offered come from the facet, so the picker can't propose a date
 * outside what the data covers.
 */
export function DateFilter({
  filter,
  label,
}: {
  filter: FilterWithFacet;
  /** The filter's resolved label, so its reset button names itself. */
  label: string;
}) {
  const t = useTranslations();
  const { selectedFilters, setRange, reset } = useSelectedFilters();

  const facet = filter.facet?.type === "range" ? filter.facet : undefined;
  const selected = selectedFilters[filter.def.name];

  const min = asDate(facet?.min);
  const max = asDate(facet?.max);
  const from = asDate(selected?.[0]) || min;
  const to = asDate(selected?.[1]) || max;

  const commit = (start: string, end: string) =>
    setRange(filter.def.name, [start, end]);

  return (
    <div className="w-64 p-4">
      <div className="flex flex-col gap-2">
        <label className="flex flex-col text-sm">
          {t("search.filters.from")}
          <input
            type="date"
            className="mt-1 rounded border border-black p-1 text-sm shadow-inner dark:text-black"
            value={from}
            min={min || undefined}
            max={to || max || undefined}
            onChange={(event) => commit(event.target.value, to)}
          />
        </label>

        <label className="flex flex-col text-sm">
          {t("search.filters.to")}
          <input
            type="date"
            className="mt-1 rounded border border-black p-1 text-sm shadow-inner dark:text-black"
            value={to}
            min={from || min || undefined}
            max={max || undefined}
            onChange={(event) => commit(from, event.target.value)}
          />
        </label>
      </div>

      <button
        type="button"
        onClick={() => reset(filter.def.name)}
        aria-label={`${t("search.filters.clear")}: ${label}`}
        className="mt-3 cursor-pointer font-semibold hover:text-primary hidden tablet:inline-flex"
      >
        {t("search.filters.clear")}
      </button>
    </div>
  );
}
